import { useParams, Link, Navigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { CookieConsent } from "@/components/CookieConsent";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MapPin, Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import whatsappIcon from "@/assets/whatsapp-outline-green.png";
import { standorte } from "@/data/standorte";

const services = [
  { title: "Fassadensanierung & WDVS", link: "/fassadensanierung-wdvs-herford" },
  { title: "Innendämmung & Schimmelschutz", link: "/innendaemmung-schimmelschutz-herford" },
  { title: "Energetische Sanierung", link: "/energetische-sanierung-herford" },
  { title: "Malerarbeiten & Innensanierung", link: "/innensanierung-malerarbeiten-herford" },
  { title: "Badrenovierung & fugenlose Bäder", link: "/badrenovierung-fugenlose-baeder-herford" },
  { title: "Edle Oberflächen & Designböden", link: "/edle-oberflaechen-designboeden-herford" },
];

const Standort = () => {
  const { slug } = useParams<{ slug: string }>();
  const normalize = (v: string) =>
    decodeURIComponent(v)
      .toLowerCase()
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .replace(/[^a-z0-9-]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  const normalized = slug ? normalize(slug) : "";
  const ort = standorte.find((s) => s.slug === normalized);

  if (!ort) return <Navigate to="/" replace />;

  const canonical = `https://malerfachbetrieb-stein.de/maler-${ort.slug}`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `Malerfachbetrieb Stein – Maler in ${ort.city}`,
    image: "https://malerfachbetrieb-stein.de/og-image.jpg",
    "@id": canonical,
    url: canonical,
    telephone: "+4952213832130",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Mindener Str. 183",
      postalCode: "32049",
      addressLocality: "Herford",
      addressCountry: "DE",
    },
    areaServed: {
      "@type": "City",
      name: ort.city,
    },
    description: `Malerfachbetrieb Stein – Ihr Malermeister für ${ort.city}: Fassadendämmung, Schimmelsanierung, Innenarbeiten und edle Oberflächen.`,
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={`Maler ${ort.city}: Fassade, Dämmung & Innenarbeiten | Stein`}
        description={`Malermeister Stein für ${ort.city} (${ort.postalRange}): Fassadensanierung, WDVS, Innendämmung, Schimmelschutz & edle Oberflächen. ☎ 05221 3832130 – 24h Rückruf.`}
        keywords={`Maler ${ort.city}, Malerfachbetrieb ${ort.city}, Fassadensanierung ${ort.city}, WDVS ${ort.city}, Schimmelsanierung ${ort.city}, Innendämmung ${ort.city}`}
        canonical={canonical}
        structuredData={structuredData}
      />
      <Navigation />
      <WhatsAppButton />

      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-foreground text-background">
          <div className="container mx-auto">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-background/60 mb-6">
                <MapPin className="h-4 w-4" />
                <span>Standort / {ort.postalRange}</span>
              </div>
              <h1 className="font-caslon text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Malermeister in <em className="not-italic text-background/70">{ort.city}</em>.
              </h1>
              <p className="text-lg md:text-xl text-background/70 leading-relaxed mb-10">
                {ort.intro}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="tel:+4952213832130"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-semibold bg-background text-foreground hover:bg-background/90 transition-all"
                >
                  <Phone className="h-5 w-5" />
                  05221 / 3832130
                </a>
                <a
                  href="https://wa.me/491788783369"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all hover:opacity-90"
                  style={{ backgroundColor: "#22c55e", color: "#ffffff" }}
                >
                  <img src={whatsappIcon} alt="WhatsApp Kontakt" className="h-5 w-5" />
                  WhatsApp Anfrage
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Schwerpunkte */}
        <section className="py-20 md:py-28">
          <div className="container mx-auto">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-4 block">
                Schwerpunkte in {ort.city}
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground">
                Womit wir in {ort.city} typischerweise beauftragt werden.
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {ort.highlights.map((h, i) => (
                <Card key={i} className="p-6 border-foreground/10 rounded-2xl">
                  <CheckCircle2 className="h-6 w-6 text-foreground mb-4" />
                  <p className="font-medium text-foreground leading-relaxed">{h}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Leistungen */}
        <section className="py-20 md:py-28 bg-muted/20">
          <div className="container mx-auto">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-4 block">
                Leistungen für {ort.city}
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground mb-4">
                Alle Leistungen aus einer Hand.
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Vom Fassaden-Check bis zur Komplettsanierung – wir betreuen Privatkunden, WEG-Verwaltungen und Gewerbe in {ort.city} und Umgebung.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-px bg-foreground/10 border border-foreground/10 rounded-2xl overflow-hidden">
              {services.map((s) => (
                <Link
                  key={s.link}
                  to={s.link}
                  className="bg-background p-6 md:p-8 flex items-center justify-between hover:bg-muted/30 transition-colors group"
                >
                  <span className="font-caslon text-xl md:text-2xl font-bold text-foreground">{s.title}</span>
                  <ArrowRight className="h-5 w-5 text-foreground/60 group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Ortsteile */}
        {ort.districts && ort.districts.length > 0 && (
          <section className="py-20 md:py-28">
            <div className="container mx-auto">
              <div className="max-w-3xl mb-10">
                <span className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-4 block">
                  Einsatzgebiet
                </span>
                <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground">
                  Wir arbeiten in allen Stadtteilen von {ort.city}.
                </h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {ort.districts.map((d) => (
                  <span
                    key={d}
                    className="px-4 py-2 border border-foreground/15 rounded-full text-sm font-medium text-foreground"
                  >
                    {d}
                  </span>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-20 md:py-28 bg-foreground text-background">
          <div className="container mx-auto text-center max-w-3xl">
            <h2 className="font-caslon text-3xl md:text-5xl font-bold mb-6">
              Projekt in {ort.city}? Wir rufen innerhalb 24h zurück.
            </h2>
            <p className="text-lg text-background/70 mb-10">
              Kostenloser Fassaden-Check oder Vor-Ort-Beratung – unverbindlich und persönlich.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" className="bg-background text-foreground hover:bg-background/90 rounded-2xl">
                <Link to="/kontakt">Kostenlosen Fassaden-Check anfordern</Link>
              </Button>
              <a
                href="tel:+4952213832130"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-semibold border border-background/30 hover:bg-background/10 transition-all"
              >
                <Phone className="h-5 w-5" />
                05221 / 3832130
              </a>
            </div>
          </div>
        </section>

        {/* Andere Standorte */}
        <section className="py-16 md:py-20 bg-muted/20">
          <div className="container mx-auto">
            <h2 className="font-caslon text-2xl md:text-3xl font-bold text-foreground mb-8">
              Weitere Standorte
            </h2>
            <div className="flex flex-wrap gap-2">
              {standorte
                .filter((s) => s.slug !== ort.slug)
                .map((s) => (
                  <Link
                    key={s.slug}
                    to={`/maler-${s.slug}`}
                    className="px-4 py-2 bg-background border border-foreground/15 rounded-full text-sm font-medium text-foreground hover:bg-foreground hover:text-background transition-colors"
                  >
                    Maler {s.city}
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default Standort;
